import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from "react";
import { Popup as AntdMobilePopup } from "antd-mobile";
import { useLocation } from "react-router-dom";
import { useUserStore } from "../../modules/user/stores";
import { openLoginRegisterPopup } from "../LoginRegisterPopup";
import {
  WITHDRAW_POPUP_EVENT,
  CLOSE_WITHDRAW_POPUP_EVENT,
} from "./triggerWithdrawPopup";
import type {
  WithdrawPopupProps,
  OpenWithdrawPopupOptions,
} from "./WithdrawPopup.types";
import ContentSkeleton from "../ContentSkeleton/ContentSkeleton";
import { useT } from "@/i18n/hooks";
import "./WithdrawPopup.scss";

const LazyWithdraw = lazy(() => import("../../pages/Withdraw/Withdraw"));

export const WithdrawPopup: React.FC<WithdrawPopupProps> = ({
  className = "",
}) => {
  const t = useT();
  const location = useLocation();
  const isAuthenticated = useUserStore(state => state.isAuthenticated());
  const [visible, setVisible] = useState(false);
  const [key, setKey] = useState(0);
  const [openOptions, setOpenOptions] = useState<OpenWithdrawPopupOptions>({});
  const openedAtPathRef = useRef<string>("");

  const handleOpen = useCallback(
    (e: Event) => {
      if (!isAuthenticated) {
        openLoginRegisterPopup({ defaultTab: "password" });
        return;
      }
      const detail = (e as CustomEvent<OpenWithdrawPopupOptions>).detail;
      setOpenOptions(detail || {});
      openedAtPathRef.current = window.location.pathname;
      setKey(prev => prev + 1);
      setVisible(true);
    },
    [isAuthenticated]
  );

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  useEffect(() => {
    window.addEventListener(WITHDRAW_POPUP_EVENT, handleOpen);
    window.addEventListener(CLOSE_WITHDRAW_POPUP_EVENT, handleClose);
    return () => {
      window.removeEventListener(WITHDRAW_POPUP_EVENT, handleOpen);
      window.removeEventListener(CLOSE_WITHDRAW_POPUP_EVENT, handleClose);
    };
  }, [handleOpen, handleClose]);

  useEffect(() => {
    if (
      visible &&
      openedAtPathRef.current &&
      location.pathname !== openedAtPathRef.current
    ) {
      setVisible(false);
    }
  }, [location.pathname, visible]);

  return (
    <AntdMobilePopup
      visible={visible}
      onMaskClick={handleClose}
      position="bottom"
      destroyOnClose
      className={`withdraw-popup ${className}`}
      maskClassName="withdraw-popup__mask"
      bodyClassName="withdraw-popup__body"
      bodyStyle={{
        height: "80vh",
        minHeight: "80vh",
        maxHeight: "80vh",
        width: "100%",
        boxSizing: "border-box",
        borderRadius: "1rem 1rem 0 0",
      }}
    >
      <div className="withdraw-popup__inner">
        <div className="withdraw-popup__sheet">
          <div className="withdraw-popup__title">{t("withdraw.title")}</div>
          <div className="withdraw-popup__drag-bar">
            <span className="withdraw-popup__drag-indicator" />
          </div>

          <div className="withdraw-popup__header">
            <button
              type="button"
              className="withdraw-popup__close"
              onClick={handleClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className="withdraw-popup__insurance-wrap" />

          <div className="withdraw-popup__scroll">
            <Suspense fallback={<ContentSkeleton />}>
              <LazyWithdraw
                key={key}
                hideWalletInfo={false}
                showBalanceOnly={false}
                isInPopup={true}
              />
            </Suspense>
          </div>
        </div>
      </div>
    </AntdMobilePopup>
  );
};

export default WithdrawPopup;
